// frontend/src/testing/annuity-whale-frontend.tsx
import React, { useState, useEffect } from 'react';
import { MapPin, Users, CheckCircle, AlertTriangle, Calendar, DollarSign, BarChart, PieChart, ChevronDown } from 'lucide-react';

const AnnuityWhaleLeadDashboard = () => {
  // State management
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [userData, setUserData] = useState({
    name: 'Jake Mossing',
    claimedCounties: ['Maricopa County, AZ'],
    leads: [
      { id: 1, name: 'John D.', county: 'Maricopa County, AZ', value: '$250,000', status: 'New', date: '04/10/2025' },
      { id: 2, name: 'Sarah M.', county: 'Maricopa County, AZ', value: '$175,000', status: 'Contacted', date: '04/09/2025' },
      { id: 3, name: 'Robert T.', county: 'Maricopa County, AZ', value: '$400,000', status: 'Meeting Scheduled', date: '04/08/2025' },
      { id: 4, name: 'Patricia L.', county: 'Maricopa County, AZ', value: '$225,000', status: 'New', date: '04/07/2025' },
    ],
    stats: {
      leadsThisMonth: 7,
      leadsConverted: 2,
      averageValue: '$217,500',
      conversionRate: '28%'
    }
  });
  
  const [availableCounties, setAvailableCounties] = useState([
    { id: 1, name: 'Franklin County, OH', status: 'available', leadsPerMonth: '3-5', price: '$350' },
    { id: 2, name: 'Denver County, CO', status: 'available', leadsPerMonth: '4-7', price: '$375' },
    { id: 3, name: 'Wake County, NC', status: 'available', leadsPerMonth: '2-4', price: '$325' },
    { id: 4, name: 'Maricopa County, AZ', status: 'claimed', leadsPerMonth: '5-9', price: '$400' },
    { id: 5, name: 'King County, WA', status: 'available', leadsPerMonth: '6-8', price: '$425' },
  ]);

  // Handle tab selection
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'Contacted': return 'bg-yellow-100 text-yellow-700';
      case 'Meeting Scheduled': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <header className="bg-blue-900 text-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="text-xl font-bold">Annuity Whale</div>
          </div>
          <div className="flex items-center space-x-4">
            <button className="bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-md text-sm font-medium">
              Claim New County
            </button>
            <div className="relative">
              <span className="text-sm font-medium">Hello, {userData.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => handleTabChange('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'leads'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Leads
            </button>
            <button
              onClick={() => handleTabChange('territories')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'territories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Territory Map
            </button>
            <button
              onClick={() => handleTabChange('marketplace')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'marketplace'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              County Marketplace
            </button>
            <button
              onClick={() => handleTabChange('performance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Report Performance
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Dashboard */}
        {selectedTab === 'dashboard' && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Advisor Dashboard</h1>
            
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                    <Users size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Leads This Month</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsThisMonth}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100 text-green-600">
                    <CheckCircle size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Converted Leads</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsConverted}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                    <DollarSign size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg. Lead Value</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.averageValue}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <AlertTriangle size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.conversionRate}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Territories */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Your Claimed Territories</h2>
              </div>
              <div className="p-6">
                {userData.claimedCounties.length > 0 ? (
                  <div className="space-y-4">
                    {userData.claimedCounties.map((county, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center">
                          <MapPin className="text-blue-600 mr-2" size={20} />
                          <span className="font-medium">{county}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-sm bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded">
                            View Stats
                          </button>
                          <button className="text-sm bg-blue-600 text-white px-3 py-1 rounded">
                            Manage Leads
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">You haven't claimed any territories yet.</p>
                    <button className="mt-2 bg-blue-600 text-white px-4 py-2 rounded">
                      Claim Your First County
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Recent Lead Activity</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {userData.leads.slice(0, 3).map((lead) => (
                  <div key={lead.id} className="p-6 flex items-center">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {lead.name} - {lead.county}
                      </p>
                      <p className="text-sm text-gray-500">
                        Annuity Value: {lead.value} · {lead.date}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-4 border-t border-gray-200">
                <button 
                  onClick={() => handleTabChange('leads')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all leads →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leads */}
        {selectedTab === 'leads' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
                  Export Leads
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Lead
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              County
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Value
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {userData.leads.map((lead) => (
                            <tr key={lead.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{lead.county}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{lead.value}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{lead.date}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                                  {lead.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex space-x-2 justify-end">
                                  <button className="text-blue-600 hover:text-blue-900">View</button>
                                  <button 
                                    onClick={() => {
                                      setCurrentLead(lead);
                                      setShowReportModal(true);
                                    }}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Report Outcome
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Territory Map Placeholder */}
        {selectedTab === 'territories' && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Territory Map</h1>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <div className="text-gray-500">
                  <p className="text-lg font-medium mb-2">Interactive Map Coming Soon</p>
                  <p>The interactive territory map will allow you to view available counties and your claimed territories.</p>
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg inline-block mx-auto">
                    <p className="font-medium text-blue-700">Your Claimed County:</p>
                    <p className="text-blue-800 font-bold mt-1">Maricopa County, AZ</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Reporting */}
        {selectedTab === 'performance' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Performance Reporting</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <button className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700">
                  Download Performance Report
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Performance Dashboard</h3>
                <p className="text-sm text-gray-500 mt-1">Track your lead conversion metrics and help us improve our lead quality.</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-800 mb-4">Lead Conversion Stats</h4>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Leads Received</p>
                        <div className="flex items-center mt-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '100%' }}></div>
                          </div>
                          <span className="ml-2 text-sm font-medium">25</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Initial Contact Made</p>
                        <div className="flex items-center mt-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '80%' }}></div>
                          </div>
                          <span className="ml-2 text-sm font-medium">20</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Meetings Scheduled</p>
                        <div className="flex items-center mt-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '60%' }}></div>
                          </div>
                          <span className="ml-2 text-sm font-medium">15</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Contracts Signed</p>
                        <div className="flex items-center mt-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '28%' }}></div>
                          </div>
                          <span className="ml-2 text-sm font-medium">7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-800 mb-4">Total Contract Value</h4>
                    <div className="flex items-center justify-center h-40">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-green-600">$1,650,000</p>
                        <p className="text-sm text-gray-500 mt-2">Total value of contracts from Annuity Whale leads</p>
                        <p className="text-xl font-semibold text-green-600 mt-4">ROI: 18.8x</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Report Lead Outcomes</h3>
                <p className="text-sm text-gray-500 mt-1">Help us improve our lead quality by reporting the outcome of your leads.</p>
              </div>
              
              <div className="p-6">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Reporting lead outcomes helps us improve lead quality and increases your ROI over time. 
                        We use this data to optimize our targeting and provide you with better leads.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userData.leads.map((lead) => (
                        <tr key={lead.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                            <div className="text-sm text-gray-500">{lead.county}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{lead.date}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                              {lead.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button 
                              onClick={() => {
                                setCurrentLead(lead);
                                setShowReportModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50"
                            >
                              Update Status
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* County Marketplace */}
        {selectedTab === 'marketplace' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">County Marketplace</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="flex space-x-2">
                  <input 
                    type="text" 
                    placeholder="Search counties..." 
                    className="border border-gray-300 rounded-md px-4 py-2 text-sm" 
                  />
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
                    Search
                  </button>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Available Counties</h3>
                <p className="text-sm text-gray-500 mt-1">Claim exclusive rights to these territories and start receiving leads.</p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {availableCounties
                  .filter(county => county.status === 'available')
                  .map((county) => (
                  <div key={county.id} className="p-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{county.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Expected leads: <span className="font-medium">{county.leadsPerMonth} per month</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-blue-700">${county.price}<span className="text-sm font-normal text-gray-500">/lead</span></p>
                      <button className="mt-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium">
                        Claim County
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Claimed Counties</h3>
                <p className="text-sm text-gray-500 mt-1">These territories are already claimed by other advisors.</p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {availableCounties
                  .filter(county => county.status === 'claimed')
                  .map((county) => (
                  <div key={county.id} className="p-6 flex items-center justify-between bg-gray-50">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700">{county.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Expected leads: <span className="font-medium">{county.leadsPerMonth} per month</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-400">${county.price}<span className="text-sm font-normal text-gray-500">/lead</span></p>
                      <button className="mt-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed">
                        Already Claimed
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Lead Outcome Reporting Modal */}
      {showReportModal && currentLead && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Report Lead Outcome</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Lead Information</p>
                <p className="text-sm text-gray-600">{currentLead.name} - {currentLead.county}</p>
                <p className="text-sm text-gray-600">Annuity Value: {currentLead.value}</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Status
                </label>
                <div className="relative">
                  <select
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    defaultValue={currentLead.status}
                  >
                    <option>New</option>
                    <option>Contacted</option>
                    <option>Meeting Scheduled</option>
                    <option>Meeting Held</option>
                    <option>Proposal Sent</option>
                    <option>Contract Signed</option>
                    <option>Contract Declined</option>
                    <option>No Response</option>
                    <option>Bad Lead</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Amount (if signed)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Quality Rating
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      className="w-8 h-8 rounded-full bg-gray-200 hover:bg-yellow-400 flex items-center justify-center text-sm font-medium"
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">1 = Poor quality, 5 = Excellent quality</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Comments
                </label>
                <textarea
                  rows={3}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Any additional feedback on this lead..."
                ></textarea>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Here you would handle the submission logic
                  setShowReportModal(false);
                  // Show success message, etc.
                }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnuityWhaleLeadDashboard;